// Import necessary React hooks and icons
import {
  AlertCircle,
  ArrowUpCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  FileText,
  Filter,
  Paperclip,
  Search,
  Send,
  TrendingUp,
  User,
  X,
  XCircle,
  AlertTriangle,
  Plus,
  Upload,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { apiFetch } from "../context/AuthContext";

// Import theme context for dark/light mode
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
// Define API base URL
const API_URL = process.env.REACT_APP_BASE_URL;
// ===== RESPONSIVE HELPER FUNCTIONS =====
const getScreenSize = () => {
  const width = window.innerWidth;
  if (width <= 480) return "xs";
  if (width <= 768) return "sm";
  if (width <= 1024) return "md";
  if (width <= 1366) return "lg";
  if (width <= 1920) return "xl";
  return "xxl";
};
const getResponsiveSpacing = (screenSize) => {
  const spacingMap = {
    xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
    sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
    md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
    lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
    xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
    xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
  };
  return spacingMap[screenSize] || spacingMap.lg;
};
const getResponsiveFontSizes = (screenSize) => {
  const fontMap = {
    xs: {
      title: "1.25rem",
      subtitle: "0.875rem",
      body: "0.75rem",
      small: "0.6875rem",
      tiny: "0.625rem",
    },
    sm: {
      title: "1.5rem",
      subtitle: "1rem",
      body: "0.875rem",
      small: "0.75rem",
      tiny: "0.6875rem",
    },
    md: {
      title: "1.75rem",
      subtitle: "1.125rem",
      body: "0.9375rem",
      small: "0.8125rem",
      tiny: "0.75rem",
    },
    lg: {
      title: "2rem",
      subtitle: "1.25rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xl: {
      title: "2.25rem",
      subtitle: "1.375rem",
      body: "1rem",
      small: "0.875rem",
      tiny: "0.8125rem",
    },
    xxl: {
      title: "2.5rem",
      subtitle: "1.5rem",
      body: "1.125rem",
      small: "1rem",
      tiny: "0.875rem",
    },
  };
  return fontMap[screenSize] || fontMap.lg;
};

// Main Escalation Complaints Component
function EscalationComplaints() {
  const apiBaseUrl = process.env.REACT_APP_BASE_URL;
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  // ===== TRANSLATIONS =====
  const text = {
    en: {
      // Header
      title: "My Escalations",
      subtitle: "Track and manage your escalated complaints to higher management",

      // Buttons
      createEscalation: "Create New Escalation",
        cancelEscalation: "Cancel",
        viewDetails: "View Details",
      submitting: "Submitting...",
      submit: "Submit ",

      // Tabs
      myEscalations: "My Escalations",
      assignedEscalations: "Assigned Escalations",

      // Status
      statusNotAssigned: "Not Assigned",
      statusPending: "Pending",
      statusInProgress: "In Progress",
      statusResolved: "Resolved",
      statusRejected: "Rejected",

      // Filters
      searchPlaceholder: "Search by ID, type...",
      allStatuses: "All Statuses",
      allPriorities: "All Priorities",
      filterByStatus: "Filter by Status",
      filterByPriority: "Filter by Priority",
            sortByDate: "Order by date",


      // Statistics
      totalEscalations: "Total Escalations",
      pendingEscalations: "Pending",
      resolvedEscalations: "Resolved",
      inProgressEscalations: "In Progress",
      updateStatus: "Update Status",
      allTypes: "All Types",
      allStatus: "All Status",
      newestFirst: "Newest First",
      oldestFirst: "Oldest First",
      groupLabel: "Group",
      allGroups: "All Groups",
      selectType: "Select type",

      // Table Headers
      escalationId: "ID",
      subject: "Subject",
      status: "Status",
      priority: "Priority",
      createdDate: "Created",
      lastUpdate: "Last Update",
      actions: "Actions",
      escalationLevel: "Level",
      assignedTo: "Assigned To",

      // Actions
      viewDetails: "View Details",
      cancelEscalation: "Cancel",
      followUp: "Follow-up",

      // Empty States
      noEscalations: "No escalations found",
      noEscalationsMessage: "You haven't created any escalations yet",
      noSentEscalations: "No sent escalations",
      noSentEscalationsMessage: "No escalations have been sent to management",

      // Modal - Create Escalation
      createEscalationTitle: "Create New Escalation",
      escalationDetails: "Escalation Details",
      employeeName: "Employee Name",
      employeeId: "Employee ID",
      department: "Department",
      escalationType: "Escalation Type",
      escalationReason: "Escalation Reason",
      selectReason: "Select Reason",
      subject: "Subject",
      description: "Description",
      priority: "Priority",
      selectPriority: "Select Priority",
      attachments: "Attachments",
      uploadFiles: "Upload Files",
      escalationHistory: "Escalation History",
      currentStatus: "Current Status",
      cancel: "Cancel",
      close: "Close",

      // Placeholders
      enterSubject: "Enter escalation subject",
      describeIssue: "Describe your issue in detail...",
      additionalDetails: "Provide any additional details that may help resolve this issue",

      // Messages
      loading: "Loading...",
      confirmCancel: "Are you sure you want to cancel this escalation?",
      escalationCancelled: "Escalation cancelled successfully",
      failedToCancel: "Failed to cancel escalation",
      escalationCreated: "Escalation created successfully!",
      failedToCreate: "Failed to create escalation",
      subjectRequired: "Subject is required",
      reasonRequired: "Reason is required",
      descriptionRequired: "Description is required",
      priorityRequired: "Priority is required",
      type_abusive: "Abusive Behaviour",
      type_employee_complain: "Employee Complaint",
      type_ticket_issue: "Ticket Issue",
      type_policy_violation: "Policy Violation",
      type_technical_issue: "Technical Issue",
      type_other: "Other",
      na: "N/A",
      status: "Status",
      detail_type: "Type",
      detail_status: "Status",
      detail_group: "Group",
      detail_issuer_sr: "Issuer SR",
      detail_issuer_name_ar: "Issuer Name (Arabic)",
      detail_issued_at: "Issued At",
      detail_description: "Description",
      update_status: "Update Status",
      mark_in_progress: "Mark In Progress",
      mark_resolved: "Mark Resolved",
      mark_rejected: "Mark Rejected",
      no_description_provided: "No description provided",
    },
    ar: {
      // Header
      title: "شكاوي",
      subtitle: "تتبع وإدارة الشكاوى أمام الجهات المختصة",

      // Buttons
      createEscalation: "شكوى جديدة",
      submit: "إرسال الشكوى",
      submitting: "جاري الإرسال...",

      // Tabs
      myEscalations: "شكاويي",
      sentEscalations: "الشكاوى المرسلة",
      assignedEscalations: "الشكاوى المسندة",

      // Status
      statusNotAssigned: "غير معين",
      statusPending: "قيد الانتظار",
      statusInProgress: "قيد المعالجة",
      statusResolved: "تم الحل",
      statusRejected: "مرفوض",

      // Priority
      priorityLow: "منخفضة",
      priorityMedium: "متوسطة",
      priorityHigh: "عالية",
      priorityCritical: "حرجة",

      // Complaint Reasons (Predefined)
      reasonSalary: "مشكلة في الراتب",
      reasonLeave: "طلب إجازة",
      reasonWorkload: "عبء عمل زائد",
      reasonHarassment: "تحرش",
      reasonDiscrimination: "تمييز",
      reasonSafety: "مشكلة أمان",
      reasonEquipment: "مشكلة في المعدات",
      reasonTraining: "طلب تدريب",
      reasonPerformance: "تقييم الأداء",
      reasonOther: "أخرى",

      // Filters
      searchPlaceholder: "البحث بالرقم، الموضوع...",
      allStatuses: "كل الحالات",
      allPriorities: "كل الأولويات",
      filterByStatus: "تصفية حسب الحالة",
      filterByPriority: "تصفية حسب الأولوية",

      // Statistics
      totalEscalations: "إجمالي الشكاوى",
      pendingEscalations: "قيد الانتظار",
      resolvedEscalations: "تم الحل",
      inProgressEscalations: "قيد المعالجة",
      updateStatus: "تحديث الحالة",
      allTypes: "كل الأنواع",
      allStatus: "كل الحالات",
      sortByDate: "رتب حسب التاريخ",
      newestFirst: "الأحدث أولا",
      oldestFirst: "الأقدم أولا",
      groupLabel: "المجموعة",
      allGroups: "كل المجموعات",
      selectType: "اختر النوع",

      // Table Headers
      escalationId: "الرقم",
      subject: "الموضوع",
      status: "الحالة",
      priority: "الأولوية",
      createdDate: "تاريخ الإنشاء",
      lastUpdate: "آخر تحديث",
      actions: "الإجراءات",
      escalationLevel: "المستوى",
      assignedTo: "مسند إلى",

      // Actions
      viewDetails: "عرض التفاصيل",
      cancelEscalation: "إلغاء",
      followUp: "متابعة",

      // Empty States
      noEscalations: "لا توجد شكاوى",
      noEscalationsMessage: "لم تقم بتقديم أي شكاوى بعد",
      noSentEscalations: "لا توجد شكاوى مرسلة",
      noSentEscalationsMessage: "لم يتم إرسال أي شكاوى للإدارة",

      // Modal - Create Complaint
      createEscalationTitle: "إنشاء شكوى جديدة",
      escalationDetails: "تفاصيل الشكوى",
      employeeName: "اسم الموظف",
      employeeId: "رقم الموظف",
      department: "القسم",
      escalationType: "نوع الشكوى",
      escalationReason: "سبب الشكوى",
      selectReason: "اختر السبب",
      subject: "الموضوع",
      description: "الوصف",
      priority: "الأولوية",
      selectPriority: "اختر الأولوية",
      attachments: "المرفقات",
      uploadFiles: "رفع الملفات",
      escalationHistory: "سجل الشكوى",
      currentStatus: "الحالة الحالية",
      cancel: "إلغاء",
      close: "إغلاق",

      // Placeholders
      enterSubject: "أدخل موضوع الشكوى",
      describeIssue: "اشرح المشكلة بالتفصيل...",
      additionalDetails: "قدم أي تفاصيل إضافية قد تساعد في حل هذه المشكلة",

      // Messages
      loading: "جاري التحميل...",
      confirmCancel: "هل أنت متأكد من إلغاء هذه الشكوى؟",
      escalationCancelled: "تم إلغاء الشكوى بنجاح",
      failedToCancel: "فشل في إلغاء الشكوى",
      escalationCreated: "تم تقديم الشكوى بنجاح!",
      failedToCreate: "فشل في تقديم الشكوى",
      subjectRequired: "الموضوع مطلوب",
      reasonRequired: "السبب مطلوب",
      descriptionRequired: "الوصف مطلوب",
      priorityRequired: "الأولوية مطلوبة",
      type_abusive: "سلوك مسيء",
      type_employee_complain: "شكوى موظف",
      type_ticket_issue: "مشكلة في التذكرة",
      type_policy_violation: "انتهاك سياسة",
      type_technical_issue: "مشكلة فنية",
      type_other: "أخرى",
      na: "غير متاح",
      status: "الحالة",
      detail_type: "النوع",
      detail_status: "الحالة",
      detail_group: "المجموعة",
      detail_issuer_sr: "رقم المبلّغ",
      detail_issuer_name_ar: "اسم المبلّغ (بالعربية)",
      detail_issued_at: "تاريخ الإرسال",
      detail_description: "الوصف",
      update_status: "تحديث الحالة",
      mark_in_progress: "وضع قيد المعالجة",
      mark_resolved: "وضع تم الحل",
      mark_rejected: "وضع مرفوض",
      no_description_provided: "لا يوجد وصف مقدم",
    },
    statusLabels: {
      notAssigned: "Not Assigned",
      pending: "Pending",
      inProgress: "In Progress",
      resolved: "Resolved",
      rejected: "Rejected",
    },
  };

  const typeKeyMap = {
    'ABUSIVE BEHAVIOUR': 'type_abusive',
    'EMPLOYEE COMPLAIN': 'type_employee_complain',
    'TICKET ISSUE': 'type_ticket_issue',
    'POLICY VIOLATION': 'type_policy_violation',
    'TECHNICAL ISSUE': 'type_technical_issue',
    'OTHER': 'type_other',
  };

  const getTypeLabel = (type) => {
    if (!type) return '';
    const key = typeKeyMap[type] || null;
    return key ? (text[language][key] || type) : type;
  };

  const statusKeyMap = {
    'NOT_ASSIGNED': 'statusNotAssigned',
    'PENDING': 'statusPending',
    'IN_PROGRESS': 'statusInProgress',
    'RESOLVED': 'statusResolved',
    'REJECTED': 'statusRejected',
  };

  const getStatusLabel = (status) => {
    if (!status) return '';
    const key = statusKeyMap[status] || null;
    return key ? (text[language][key] || status.replace(/_/g, ' ')) : status.replace(/_/g, ' ');
  };
  const ko = "mo"
  // ===== STATE MANAGEMENT =====
  const [screenSize, setScreenSize] = useState(getScreenSize());
  const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
  const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));

  const [activeTab, setActiveTab] = useState("my");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [escalations, setEscalations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignedEscalations, setAssignedEscalations] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // New Escalation Form State
  const [newEscalation, setNewEscalation] = useState({
    type: "",
    description: "",
  });


  const [formErrors, setFormErrors] = useState({});

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
  });

  // ===== RESPONSIVE UPDATES =====
  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = getScreenSize();
      setScreenSize(newScreenSize);
      setSpacing(getResponsiveSpacing(newScreenSize));
      setFonts(getResponsiveFontSizes(newScreenSize));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeTab === "my") {
      fetchMyEscalations();
    }
  }, [activeTab]);

  const fetchMyEscalations = async () => {
    try {
      setLoading(true);

      const res = await apiFetch(`${apiBaseUrl}/api/escalations/my-escalations`, { method: "GET" });

      if (!res.ok) {
        throw new Error("Failed to fetch escalations");
      }

      const data = await res.json();

      // Map backend model → UI model
      const mappedEscalations = data.data.escalations.map((e) => ({
        id: e.id,
        type: e.type,
        status: e.status,
        issuedAt: e.issuedAt,
        description: e.description,
      }));

      setEscalations(mappedEscalations);

      // Update Stats
      setStats({
        total: mappedEscalations.length,
        inProgress: mappedEscalations.filter((e) => e.status === "IN_PROGRESS").length,
        pending: mappedEscalations.filter((e) => e.status === "PENDING").length,
        resolved: mappedEscalations.filter((e) => e.status === "RESOLVED").length,
        rejected: mappedEscalations.filter((e) => e.status === "REJECTED").length,
      });

    } catch (err) {
      console.error("Failed to fetch escalations:", err);
      setError("Failed to load escalations");
    } finally {
      setLoading(false);
    }
  };

  // Fetch groups on mount
  useEffect(() => {
    if (activeTab === 'assigned') {
      fetchGroups();
    }
  }, [activeTab]);

  // Fetch escalations when group changes
  useEffect(() => {
    if (activeTab === 'assigned' && groups.length > 0) {
      fetchEscalations();
    }
  }, [selectedGroup, groups, activeTab]);

  const fetchGroups = async () => {
    try {
      const res = await apiFetch(`${apiBaseUrl}/api/groups/member-of-groups`, { method: "GET" });
      const data = await res.json();
      setGroups(data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchEscalations = async () => {
    setLoading(true);
    try {
      if (selectedGroup === 'all') {
        // Fetch escalations from all groups
        const allEscalations = await Promise.all(
          groups.map(async (group) => {
            const res = await apiFetch(
              `${apiBaseUrl}/api/escalations/${group.groupId}/escalations`,
              { method: "GET" }
            );
            const data = await res.json();
            return data.data?.map(item => ({
              ...item.escalation,
              groupName: group.groupName,
              groupId: group.groupId
            })) || [];
          })
        );
        setAssignedEscalations(allEscalations.flat());
      } else {
        // Fetch escalations for selected group
        const res = await apiFetch(
          `${apiBaseUrl}/api/escalations/${selectedGroup}/escalations`,
          { method: "GET" }
        );
        const data = await res.json();
        const groupName = groups.find(g => g.groupId === selectedGroup)?.groupName;
        setAssignedEscalations(
          data.data?.map(item => ({
            ...item.escalation,
            groupName,
            groupId: selectedGroup
          })) || []
        );
      }
    } catch (error) {
      console.error('Error fetching escalations:', error);
      setEscalations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEscalation = async (data) => {
    try {
      setLoading(true);

      const res = await apiFetch(`${apiBaseUrl}/api/escalations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create escalation");

      setSuccess("Escalation created successfully");
      setShowCreateModal(false);

      fetchMyEscalations();
    } catch (err) {
      setError("Failed to create escalation");
    } finally {
      setLoading(false);
    }
  };

  // ===== CREATE ESCALATION HANDLERS =====
  const handleCreateEscalation = () => {
    setShowCreateModal(true);
    setNewEscalation({
      subject: "",
      reason: "",
      description: "",
      priority: "",
      attachments: [],
    });
    setFormErrors({});
  };


  // ===== STATUS BADGE =====
  const getStatusBadge = (status) => {
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: "0.75rem",
      fontSize: fonts.small,
      fontWeight: "600",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
    };

    const statusConfig = {
      NOT_ASSIGNED: {
        bg: "#f3f4f6",
        color: "#374151",
        icon: <AlertCircle size={12} />,
        text: text[language].statusNotAssigned || "Not Assigned",
      },
      PENDING: {
        bg: "#fef3c7",
        color: "#92400e",
        icon: <Clock size={12} />,
        text: text[language].statusPending || "Pending",
      },
      IN_PROGRESS: {
        bg: "#dbeafe",
        color: "#1e40af",
        icon: <TrendingUp size={12} />,
        text: text[language].statusInProgress || "In Progress",
      },
      RESOLVED: {
        bg: "#d1fae5",
        color: "#065f46",
        icon: <CheckCircle size={12} />,
        text: text[language].statusResolved || "Resolved",
      },
      REJECTED: {
        bg: "#fee2e2",
        color: "#991b1b",
        icon: <XCircle size={12} />,
        text: text[language].statusRejected || "Rejected",
      },
    };

    const config = statusConfig[status] || statusConfig.NOT_ASSIGNED;

    return (
      <span
        style={{
          ...baseStyle,
          backgroundColor: config.bg,
          color: config.color,
        }}
      >
        {config.icon}
        {config.text}
      </span>
    );
  };

  // ===== PRIORITY BADGE =====
  const getPriorityBadge = (priority) => {
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: "0.75rem",
      fontSize: fonts.small,
      fontWeight: "600",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
    };

    const priorityConfig = {
      Low: {
        bg: "#e0f2fe",
        color: "#0369a1",
        icon: <ArrowUpCircle size={12} style={{ transform: "rotate(180deg)" }} />,
        text: text[language].priorityLow,
      },
      Medium: {
        bg: "#fef3c7",
        color: "#92400e",
        icon: <ArrowUpCircle size={12} style={{ transform: "rotate(-90deg)" }} />,
        text: text[language].priorityMedium,
      },
      High: {
        bg: "#fed7aa",
        color: "#9a3412",
        icon: <ArrowUpCircle size={12} />,
        text: text[language].priorityHigh,
      },
      Critical: {
        bg: "#fee2e2",
        color: "#991b1b",
        icon: <AlertTriangle size={12} />,
        text: text[language].priorityCritical,
      },
    };

    const config = priorityConfig[priority] || priorityConfig.Medium;

    return (
      <span
        style={{
          ...baseStyle,
          backgroundColor: config.bg,
          color: config.color,
        }}
      >
        {config.icon}
        {config.text}
      </span>
    );
  };

  // ===== STYLES =====
  const styles = {
    container: {
      direction: language === "ar" ? "rtl" : "ltr",
    },
    header: {
      background:
        theme === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(255, 255, 255, 0.08)",
      border:
        theme === "light"
          ? "1px solid rgba(0, 0, 0, 0.1)"
          : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
      padding: spacing.padding,
      marginBottom: spacing.margin,
      boxShadow:
        theme === "light"
          ? "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1)"
          : "0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.5)",
    },
    headerContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: screenSize === "xs" ? "flex-start" : "center",
      flexDirection: screenSize === "xs" ? "column" : "row",
      gap: spacing.gap,
    },
    title: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      margin: 0,
      marginBottom: "0.5rem",
    },
    subtitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
      fontSize: fonts.subtitle,
      margin: 0,
    },
    createBtn: {
      background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.5rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      transition: "all 0.3s ease",
      boxShadow: "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)",
      whiteSpace: "nowrap",
    },
    tabContainer: {
      display: "flex",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },
    tab: {
      background: "transparent",
      border: "none",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "1rem 1.5rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
      borderBottom: "2px solid transparent",
      transition: "all 0.3s ease",
    },
    activeTab: {
      color: "#2e7cba",
      borderBottomColor: "#2e7cba",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
    },
    statCard: {
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      padding: "1.25rem",
      borderRadius: screenSize === "xs" ? "1rem" : "1rem",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      transition: "all 0.3s ease",
    },
    filterContainer: {
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : "repeat(3, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      padding: spacing.padding,
      borderRadius: "1rem",
    },
    filterInput: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.9)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: "0.5rem",
      padding: screenSize === "xs" ? "0.625rem 0.75rem" : "0.75rem 1rem",
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      outline: "none",
      width: "100%",
      
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth: screenSize === "xs" ? "600px" : "900px",
            textAlign: "center",

    },
    tableHeader: {
      background:
        theme === "dark"
          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      padding: screenSize === "xs" ? "0.75rem 0.5rem" : "1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderRadius: "0.2rem 0.05rem 0 0",
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.1)",
    },
    tableCell: {
      padding: screenSize === "xs" ? "0.75rem 0.5rem" : "1rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.06)"
          : "1px solid rgba(148, 163, 184, 0.08)",
      color:
        theme === "dark"
          ? "rgba(248, 250, 252, 0.95)"
          : "rgba(30, 41, 59, 0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
            textAlign: "center",

    },
    actionBtn: {
      background: "rgba(59, 130, 246, 0.1)",
      color: "#2e7cba",
      border: "1px solid rgba(59, 130, 246, 0.2)",
      padding: screenSize === "xs" ? "0.375rem 0.5rem" : "0.5rem 0.75rem",
      borderRadius: "0.5rem",
      cursor: "pointer",
      fontSize: fonts.small,
      fontWeight: "500",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "0.25rem",
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      zIndex: 2000,
      backdropFilter: "blur(10px)",
      padding: screenSize === "xs" ? "0" : "2rem",
      overflowY: "auto",
       scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    modalContent: {
      background: theme === "dark" ? "rgba(30, 41, 59, 0.6)" : "rgba(255, 255, 255, 0.9)",

      backdropFilter: "blur(25px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      width: screenSize === "xs" ? "100%" : "90%",
      maxWidth: "50rem",
      marginTop: screenSize === "xs" ? "0" : "0%",
      marginBottom: screenSize === "xs" ? "0" : "0%",
      maxHeight: "90vh",
      overflowY: "auto",
       scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },
    formGroup: {
      marginBottom: spacing.margin,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
      gap: spacing.gap,
    },
    sectionTitle: {
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(26, 26, 26, 0.8)",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `${spacing.margin} 0 ${screenSize === "xs" ? "0.75rem" : "1rem"
        } 0`,
      paddingBottom: "0.5rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: screenSize === "xs" ? "1.5rem" : "2rem",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      paddingBottom: screenSize === "xs" ? "0.75rem" : "1rem",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
      gap: screenSize === "xs" ? "1rem" : "0",
    },
    modalTitle: {
      color: theme === "dark" ? "white" : "#1a1a1a",
      fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
      fontWeight: "700",
      margin: 0,
    },
    modalActions: {
      display: "flex",
      gap: screenSize === "xs" ? "0.75rem" : "0.75rem",
      justifyContent: screenSize === "xs" ? "center" : "flex-end",
      marginTop: screenSize === "xs" ? "1.5rem" : "2rem",
      paddingTop: spacing.padding,
      borderTop:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
    },
    closeBtn: {
      background: "none",
      border: "none",
      color: theme === "dark" ? "white" : "#1a1a1a",
      cursor: "pointer",
      padding: "0.5rem",
      borderRadius: "0.5rem",
      transition: "background 0.2s",
    },

    formLabel: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      color: theme === "dark" ? "rgba(241, 245, 249, 0.9)" : "rgba(15, 23, 42, 0.8)",
      fontSize: fonts.body,
      fontWeight: "600",
      marginBottom: "0.625rem",
      letterSpacing: "0.01em",
    },
    formInput: {
      background: theme === "dark"
        ? "rgba(30, 41, 59, 0.8)"
        : "rgba(248, 250, 252, 0.9)",
      border: theme === "dark"
        ? "2px solid rgba(148, 163, 184, 0.2)"
        : "2px solid rgba(148, 163, 184, 0.3)",
      borderRadius: "0.75rem",
      padding: screenSize === "xs" ? "0.875rem 1rem" : "1rem 1.25rem",
      color: theme === "dark" ? "#f1f5f9" : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxSizing: "border-box",
      boxShadow: theme === "dark"
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
    },
    formTextarea: {
      background: theme === "dark"
        ? "rgba(30, 41, 59, 0.8)"
        : "rgba(248, 250, 252, 0.9)",
      border: theme === "dark"
        ? "2px solid rgba(148, 163, 184, 0.2)"
        : "2px solid rgba(148, 163, 184, 0.3)",
      borderRadius: "0.75rem",
      padding: screenSize === "xs" ? "0.875rem 1rem" : "1rem 1.25rem",
      color: theme === "dark" ? "#f1f5f9" : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      minHeight: "150px",
      resize: "vertical",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      boxSizing: "border-box",
      boxShadow: theme === "dark"
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
    },
    formSelect: {
      appearance: "none",
      background: theme === "dark"
        ? "rgba(30, 41, 59, 0.8)"
        : "rgba(248, 250, 252, 0.9)",
      border: theme === "dark"
        ? "2px solid rgba(148, 163, 184, 0.2)"
        : "2px solid rgba(148, 163, 184, 0.3)",
      borderRadius: "0.75rem",
      padding: screenSize === "xs" ? "0.875rem 1rem" : "1rem 1.25rem",
      color: theme === "dark"
        ? "#f1f5f9"
        : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxSizing: "border-box",
      boxShadow: theme === "dark"
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
        }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
      paddingRight: language === "ar" ? "2.5rem" : undefined,
      paddingLeft: language === "ar" ? "1rem" : "2.5rem",
    },
    errorText: {
      color: "#ef4444",
      fontSize: fonts.small,
      marginTop: "0.25rem",
      display: "block",
    },
    uploadArea: {
      border:
        theme === "dark"
          ? "2px dashed rgba(255, 255, 255, 0.2)"
          : "2px dashed rgba(0, 0, 0, 0.2)",
      borderRadius: "0.75rem",
      padding: screenSize === "xs" ? "1.5rem" : "2rem",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    },
    attachmentItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.75rem",
      background:
        theme === "dark" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.08)",
      border: "1px solid rgba(59, 130, 246, 0.2)",
      borderRadius: "0.5rem",
      marginTop: "0.5rem",
    },
    submitBtn: {
      background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
      color: "white",
      border: "none",
      padding: screenSize === "xs" ? "0.875rem 1.5rem" : "1rem 2rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)",
      width: "100%",
    },
    cancelBtn: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: theme === "dark" ? "white" : "#1a1a1a",
      border:
        theme === "dark"
          ? "2px solid rgba(255, 255, 255, 0.2)"
          : "2px solid rgba(0, 0, 0, 0.1)",
      padding: screenSize === "xs" ? "0.875rem 1.5rem" : "1rem 2rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      width: "100%",
    },
    emptyState: {
      textAlign: "center",
      padding: screenSize === "xs" ? "3rem 1rem" : "4rem 2rem",
      color:
        theme === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(26, 26, 26, 0.5)",
    },
  };

  // ===== CREATE ESCALATION MODAL =====
  const CreateEscalationModal = ({
    onClose,
    onSubmit,
    loading,
    theme,
    screenSize,
    styles,
    spacing,
  }) => {
    const [type, setType] = useState("");
    const [description, setDescription] = useState("");
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
      e.preventDefault();

      const errs = {};
      if (!type) errs.type = text[language].escalationType + " " + text[language].selectType.toLowerCase();
      if (!description) errs.description = text[language].description + " " + text[language].descriptionRequired.toLowerCase();

      if (Object.keys(errs).length) {
        setErrors(errs);
        return;
      }

      onSubmit({ type, description });
    };

    // Handle clicking outside modal to close it
    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    // Prevent Enter key from submitting unless on submit button
    const handleFormKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'button' || target.type === 'submit') return;
      e.preventDefault();
    };

    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>

          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{text[language].createEscalationTitle}</h2>
            <button
              style={styles.closeBtn}
              onClick={onClose}
              title={text[language].close}
            >
              <X size={screenSize === "xs" ? 20 : 24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>

            {/* Escalation Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].escalationDetails}
            </div>

            {/* Type Field */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].escalationType}
                <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  if (errors.type) setErrors({ ...errors, type: "" });
                }}
                style={{
                  ...styles.formInput,
                  borderColor: errors.type ? "#ef4444" : undefined,
                }}
              >
                <option value="">{text[language].selectType}</option>
                <option value='ABUSIVE BEHAVIOUR'>{getTypeLabel('ABUSIVE BEHAVIOUR')}</option>
                <option value="EMPLOYEE COMPLAIN">{getTypeLabel('EMPLOYEE COMPLAIN')}</option>
                <option value="TICKET ISSUE">{getTypeLabel('TICKET ISSUE')}</option>
                <option value="POLICY VIOLATION">{getTypeLabel('POLICY VIOLATION')}</option>
                <option value="TECHNICAL ISSUE">{getTypeLabel('TECHNICAL ISSUE')}</option>
                <option value="OTHER">{getTypeLabel('OTHER')}</option>
              </select>
              {errors.type && (
                <span style={styles.errorText}>
                  <AlertCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                  {errors.type}
                </span>
              )}
            </div>

            {/* Description Field */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].description}
                <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) setErrors({ ...errors, description: "" });
                }}
                style={{
                  ...styles.formTextarea,
                  borderColor: errors.description ? "#ef4444" : undefined,
                }}
                placeholder={text[language].describeIssue}
              />
              {errors.description && (
                <span style={styles.errorText}>
                  <AlertCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                  {errors.description}
                </span>
              )}
            </div>

            {/* Form Actions */}
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  ...styles.cancelBtn,
                  minWidth: screenSize === "xs" ? "100%" : "150px",
                }}
              >
                {text[language].cancel}
              </button>

              <button
                type="submit"
                style={{
                  ...styles.submitBtn,
                  minWidth: screenSize === "xs" ? "100%" : "150px",
                }}
                disabled={loading}
              >
                {loading ? text[language].submitting : text[language].submit}
              </button>
            </div>

          </form>
        </div>
      </div>
    );
  };

  const GroupAssignedEscalation = ({
    activeTab,
    screenSize,
    language,
    text,
    loading,
    theme,
    styles,
    fonts,
    getStatusBadge,
    apiBaseUrl,
    fetchEscalations
  }) => {
    const [selectedType, setSelectedType] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedEscalation, setSelectedEscalation] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const escalationTypes = [
      'ABUSIVE BEHAVIOUR',
      'EMPLOYEE COMPLAIN',
      'TICKET ISSUE',
      'POLICY VIOLATION',
      'TECHNICAL ISSUE',
      'OTHER'
    ];

    const statusOptions = ['NOT_ASSIGNED', 'PENDING', 'IN_PROGRESS', 'RESOLVED', 'REJECTED'];

    const getFilteredAndSortedEscalations = () => {
      let filtered = [...assignedEscalations];

      // Filter by type
      if (selectedType !== 'all') {
        filtered = filtered.filter(esc =>
          esc.type?.toLowerCase() === selectedType.toLowerCase()
        );
      }

      // Filter by status
      if (selectedStatus !== 'all') {
        filtered = filtered.filter(esc => esc.status == selectedStatus);
      }

      // Sort by issuedAt
      filtered.sort((a, b) => {
        const dateA = new Date(a.issuedAt);
        const dateB = new Date(b.issuedAt);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });

      return filtered;
    };

    const handleUpdateStatus = async (escalationId, newStatus) => {
      try {
        console.log("status", newStatus)
        setLoading(true);
        const res = await apiFetch(`${apiBaseUrl}/api/escalations/${escalationId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus })
        });

        if (!res.ok) throw new Error("Failed to update status");

        // Refresh escalations after update
        fetchEscalations();
        setShowDetailsModal(false);
        // You can add a success message here if you want
      } catch (error) {
        console.error('Error updating status:', error);
        // You can add an error message here if you want
      } finally {
        setLoading(false);
      }
    };

    const DetailModal = () => {
      if (!showDetailsModal || !selectedEscalation) return null;

      return (
        <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
          <div
            style={{
              ...styles.modalContent,
              maxWidth: '650px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {text[language].escalationDetails}
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={styles.closeBtn}
                title={text[language].close}
              >
                <X size={screenSize === "xs" ? 20 : 24} />
              </button>
            </div>

            {/* Escalation Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].escalationDetails}
            </div>

            {/* ID & Type Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].escalationId}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                  color: '#2e7cba',
                  fontWeight: '600',
                }}>
                  ESC-{selectedEscalation.id}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].detail_type}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {getTypeLabel(selectedEscalation.type)}
                </div>
              </div>
            </div>

            {/* Status & Group Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].detail_status}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '0.75rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                  minHeight: '44px',
                }}>
                  {getStatusBadge(selectedEscalation.status)}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].detail_group}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {selectedEscalation.groupName}
                </div>
              </div>
            </div>

            {/* Issuer Information Section */}
            <div style={styles.sectionTitle}>
              {text[language].detail_issuer_sr}
            </div>

            {/* Issuer SR & Name Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].detail_issuer_sr}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {selectedEscalation.issuerEmployee?.sr || text[language].na}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].detail_issuer_name_ar}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {selectedEscalation.issuerEmployee?.arabicName || text[language].na}
                </div>
              </div>
            </div>

            {/* Date */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].detail_issued_at}</label>
              <div style={{
                ...styles.formInput,
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                padding: '1rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'default',
              }}>
                <Calendar size={14} style={{ opacity: 0.6 }} />
                {new Date(selectedEscalation.issuedAt).toLocaleString()}
              </div>
            </div>

            {/* Description Section */}
            <div style={styles.sectionTitle}>
              {text[language].detail_description}
            </div>

            <div style={styles.formGroup}>
              <div style={{
                ...styles.formTextarea,
                minHeight: '120px',
                padding: '1.25rem',
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                border: 'none',
                cursor: 'default',
                resize: 'none',
              }}>
                {selectedEscalation.description || text[language].no_description_provided}
              </div>
            </div>

            {/* Update Status Section */}
            <div style={styles.sectionTitle}>
              {text[language].updateStatus}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: screenSize === 'xs' ? '1fr' : 'repeat(3, 1fr)',
              gap: '0.75rem',
              marginBottom: spacing.margin,
            }}>
              <button
                onClick={() => handleUpdateStatus(selectedEscalation.id, 'IN_PROGRESS')}
                disabled={loading || selectedEscalation.status === 'IN_PROGRESS'}
                style={{
                  background: selectedEscalation.status === 'IN_PROGRESS'
                    ? 'rgba(59, 130, 246, 0.3)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.875rem 1rem',
                  borderRadius: '0.75rem',
                  cursor: selectedEscalation.status === 'IN_PROGRESS' || loading ? 'not-allowed' : 'pointer',
                  fontSize: fonts.body,
                  fontWeight: '600',
                  opacity: selectedEscalation.status === 'IN_PROGRESS' || loading ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {text[language].mark_in_progress}
              </button>

              <button
                onClick={() => handleUpdateStatus(selectedEscalation.id, 'RESOLVED')}
                disabled={loading || selectedEscalation.status === 'RESOLVED'}
                style={{
                  background: selectedEscalation.status === 'RESOLVED'
                    ? 'rgba(16, 185, 129, 0.3)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.875rem 1rem',
                  borderRadius: '0.75rem',
                  cursor: selectedEscalation.status === 'RESOLVED' || loading ? 'not-allowed' : 'pointer',
                  fontSize: fonts.body,
                  fontWeight: '600',
                  opacity: selectedEscalation.status === 'RESOLVED' || loading ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {text[language].mark_resolved}
              </button>

              <button
                onClick={() => handleUpdateStatus(selectedEscalation.id, 'REJECTED')}
                disabled={loading || selectedEscalation.status === 'REJECTED'}
                style={{
                  background: selectedEscalation.status === 'REJECTED'
                    ? 'rgba(239, 68, 68, 0.3)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.875rem 1rem',
                  borderRadius: '0.75rem',
                  cursor: selectedEscalation.status === 'REJECTED' || loading ? 'not-allowed' : 'pointer',
                  fontSize: fonts.body,
                  fontWeight: '600',
                  opacity: selectedEscalation.status === 'REJECTED' || loading ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {text[language].mark_rejected}
              </button>
            </div>

            {/* Close Button */}
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  ...styles.submitBtn,
                  minWidth: screenSize === "xs" ? "100%" : "150px",
                }}
              >
                {text[language].close}
              </button>
            </div>
          </div>
        </div>
      );
    };

    if (activeTab !== 'assigned') return null;

    return (
      <>
        <div style={{ ...styles.statCard, marginTop: '1rem'  ,marginBottom: '2rem'}}>
          {/* Filters */}
          <div style={{
            display: 'flex',
            flexDirection: screenSize === 'xs' ? 'column' : 'row',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {/* Group Filter */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                <Filter size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                {text[language].groupLabel}
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="all">{text[language].allGroups}</option>
                {groups.map(group => (
                  <option key={group.groupId} value={group.groupId}>
                    {group.groupName}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                {text[language].escalationType}
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="all">{text[language].allTypes}</option>
                {escalationTypes.map(type => (
                  <option key={type} value={type}>
                    {getTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                {text[language].status}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="all">{text[language].allStatus}</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Order */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                {text[language].sortByDate}
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="desc">{text[language].newestFirst}</option>
                <option value="asc">{text[language].oldestFirst}</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' ,}}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>{text[language].escalationId}</th>
                  <th style={styles.tableHeader}>{text[language].escalationType}</th>
                  <th style={styles.tableHeader}>{text[language].groupLabel}</th>
                  <th style={styles.tableHeader}>{text[language].status}</th>
                  {screenSize !== "xs" && <th style={styles.tableHeader}>{text[language].createdDate}</th>}
                  <th style={styles.tableHeader}>{text[language].actions}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        ...styles.tableCell,
                        textAlign: "center",
                        padding: "2rem",
                      }}
                    >
                      {text[language].loading}
                    </td>
                  </tr>
                ) : getFilteredAndSortedEscalations().length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        ...styles.tableCell,
                        textAlign: "center",
                        padding: 0,
                      }}
                    >
                      <div style={styles.emptyState}>
                        <FileText
                          size={screenSize === "xs" ? 48 : 64}
                          style={{ opacity: 0.3, marginBottom: "1rem" }}
                        />
                        <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                          {text[language].noEscalations}
                        </div>
                        <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
                          {text[language].noEscalationsMessage}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getFilteredAndSortedEscalations().map((escalation) => (
                    <tr
                      key={`${escalation.groupId}-${escalation.id}`}
                      style={{ transition: "background 0.2s", cursor: "pointer" }}
                      onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td style={styles.tableCell}>
                        <strong style={{ color: "#2e7cba" }}>ESC-{escalation.id}</strong>
                      </td>

                      <td style={styles.tableCell}>{getTypeLabel(escalation.type)}</td>

                      <td style={styles.tableCell}>{escalation.groupName}</td>

                      <td style={styles.tableCell}>
                        {getStatusBadge(escalation.status)}
                      </td>

                      {screenSize !== "xs" && (
                        <td style={styles.tableCell}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <Calendar size={14} style={{ opacity: 0.6 }} />
                            {new Date(escalation.issuedAt).toLocaleDateString()}
                          </div>
                        </td>
                      )}

                      <td style={styles.tableCell}>
                        <button
                          onClick={() => {
                            setSelectedEscalation(escalation);
                            setShowDetailsModal(true);
                          }}
                          style={{
                            ...styles.submitBtn,
                            padding: "0.25rem 0.5rem",
                            fontSize: fonts.small,
                            cursor: "pointer",
                          }}
                        >
                          {text[language].viewDetails}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <DetailModal />
      </>
    );
  };

  const MyEscalationsTable = ({
    loading,
    escalations,
    screenSize,
    language,
    text,
    theme,
    styles,
    fonts,
    getStatusBadge,
  }) => {
    const [selectedType, setSelectedType] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showModal, setShowModal] = useState(false);
    const [currentEscalation, setCurrentEscalation] = useState(null);

    const escalationTypes = [
      'ABUSIVE BEHAVIOUR',
      'EMPLOYEE COMPLAIN',
      'TICKET ISSUE',
      'POLICY VIOLATION',
      'TECHNICAL ISSUE',
      'OTHER'
    ];

    const statusOptions = ['NOT_ASSIGNED', 'PENDING', 'IN_PROGRESS', 'RESOLVED', 'REJECTED'];

    const getFilteredEscalations = () => {
      let filtered = escalations;

      // Filter by type
      if (selectedType !== 'all') {
        filtered = filtered.filter(esc =>
          esc.type?.toLowerCase() === selectedType.toLowerCase()
        );
      }

      // Filter by status
      if (selectedStatus !== 'all') {
        filtered = filtered.filter(esc => esc.status === selectedStatus);
      }

      // Sort by issuedAt
      filtered.sort((a, b) => {
        const dateA = new Date(a.issuedAt);
        const dateB = new Date(b.issuedAt);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });

      return filtered;
    };

    const handleViewDetails = (escalation) => {
      setCurrentEscalation(escalation);
      setShowModal(true);
    };

    const DetailModal = () => {
      if (!showModal || !currentEscalation) return null;

      return (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div
            style={{
              ...styles.modalContent,
              maxWidth: '600px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {text[language].escalationDetails}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={styles.closeBtn}
                title={text[language].close}
              >
                <X size={screenSize === "xs" ? 20 : 24} />
              </button>
            </div>

            {/* Escalation Information Section */}
            {/* <div style={styles.sectionTitle}>
              {text[language].detail_type}
            </div> */}

            {/* ID & Type Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].escalationId}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                  color: '#2e7cba',
                  fontWeight: '600',
                }}>
                  ESC-{currentEscalation.id}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].escalationType}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {getTypeLabel(currentEscalation.type)}
                </div>
              </div>
            </div>

            {/* Status & Date Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].status}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '0.75rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                }}>
                  {getStatusBadge(currentEscalation.status)}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{text[language].createdDate}</label>
                <div style={{
                  ...styles.formInput,
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'default',
                }}>
                  <Calendar size={14} style={{ opacity: 0.6 }} />
                  {new Date(currentEscalation.issuedAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div style={styles.sectionTitle}>
              {text[language].description}
            </div>

            <div style={styles.formGroup}>
              <div style={{
                ...styles.formTextarea,
                minHeight: '120px',
                padding: '1.25rem',
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                border: 'none',
                cursor: 'default',
                resize: 'none',
              }}>
                {currentEscalation.description || text[language].na}
              </div>
            </div>

            {/* Close Button */}
            {/* <div style={styles.modalActions}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  ...styles.submitBtn,
                  minWidth: screenSize === "xs" ? "100%" : "150px",
                }}
              >
                {text[language].close}
              </button>
            </div> */}
          </div>
        </div>
      );
    };

    return (
      <>
        <div style={styles.statsGrid}>
          {/* Total Escalations */}
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                  : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)";
            }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: "0 0 0.5rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <FileText size={16} />
              {text[language].totalEscalations}
            </h4>
            <p
              style={{
                color: "#2e7cba",
                fontSize: "1.75rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stats.total}
            </p>
          </div>

          {/* Pending */}
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                  : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)";
            }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: "0 0 0.5rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <Clock size={16} />
              {text[language].pendingEscalations}
            </h4>
            <p
              style={{
                color: "#f59e0b",
                fontSize: "1.75rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stats.pending || 0}
            </p>
          </div>

          {/* In Progress */}
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                  : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)";
            }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: "0 0 0.5rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <TrendingUp size={16} />
              {text[language].inProgressEscalations}
            </h4>
            <p
              style={{
                color: "#3b82f6",
                fontSize: "1.75rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stats.inProgress || 0}
            </p>
          </div>

          {/* Resolved */}
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                  : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)";
            }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: "0 0 0.5rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <CheckCircle size={16} />
              {text[language].resolvedEscalations}
            </h4>
            <p
              style={{
                color: "#10b981",
                fontSize: "1.75rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stats.resolved || 0}
            </p>
          </div>

          {/* Rejected */}
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                  : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                theme === "dark"
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)";
            }}
          >
            <h4
              style={{
                color: theme === "dark" ? "white" : "#1a1a1a",
                margin: "0 0 0.5rem 0",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: fonts.body,
              }}
            >
              <XCircle size={16} />
              {text[language].statusRejected}
            </h4>
            <p
              style={{
                color: "#ef4444",
                fontSize: "1.75rem",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {stats.rejected || 0}
            </p>
          </div>
        </div>
        <div style={{ ...styles.statCard, marginBottom: '1rem' }}>
          {/* Filters */}
          <div style={{
            display: 'flex',
            flexDirection: screenSize === 'xs' ? 'column' : 'row',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {/* Type Filter */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                <Filter size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                {text[language].escalationType}
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="all">{text[language].allTypes}</option>
                {escalationTypes.map(type => (
                  <option key={type} value={type}>
                    {getTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                {text[language].status}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="all">{text[language].allStatus}</option>
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Order */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: fonts.small,
                opacity: 0.8
              }}>
                {text[language].sortByDate}
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  appearance: "none",
                  background: theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(255, 255, 255, 0.95)",
                  border: theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(148, 163, 184, 0.2)",
                  borderRadius: "0.75rem",
                  padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
                  color: theme === "dark"
                    ? "rgba(248, 250, 252, 0.95)"
                    : "rgba(30, 41, 59, 0.9)",
                  fontSize: fonts.body,
                  outline: "none",
                  width: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                    }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
                }}
              >
                <option value="desc">{text[language].newestFirst}</option>
                <option value="asc">{text[language].oldestFirst}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            ...styles.statCard,
            padding: 0,
            overflow: "hidden",
            marginBottom: "9rem",
          }}
        >
          <div style={{ overflowX: "auto",            marginBottom: "9rem",
 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>{text[language].escalationId}</th>
                  <th style={styles.tableHeader}>{text[language].escalationType}</th>
                  <th style={styles.tableHeader}>{text[language].status}</th>
                  {screenSize !== "xs" && <th style={styles.tableHeader}>{text[language].createdDate}</th>}
                  <th style={styles.tableHeader}>{text[language].actions}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        ...styles.tableCell,
                        textAlign: "center",
                        padding: "2rem",
                      }}
                    >
                      {text[language].loading}
                    </td>
                  </tr>
                ) : getFilteredEscalations().length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        ...styles.tableCell,
                        textAlign: "center",
                        padding: 0,
                      }}
                    >
                      <div style={styles.emptyState}>
                        <FileText
                          size={screenSize === "xs" ? 48 : 64}
                          style={{ opacity: 0.3, marginBottom: "1rem" }}
                        />
                        <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                          {text[language].noEscalations}
                        </div>
                        <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
                          {text[language].noEscalationsMessage}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getFilteredEscalations().map((escalation) => (
                    <tr
                      key={escalation.id}
                      style={{ transition: "background 0.2s", cursor: "pointer" }}
                      onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td style={styles.tableCell}>
                        <strong style={{ color: "#2e7cba" }}>ESC-{escalation.id}</strong>
                      </td>

                      <td style={styles.tableCell}>{getTypeLabel(escalation.type)}</td>

                      <td style={styles.tableCell}>{getStatusBadge(escalation.status)}</td>

                      {screenSize !== "xs" && (
                        <td style={styles.tableCell}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <Calendar size={14} style={{ opacity: 0.6 }} />
                            {new Date(escalation.issuedAt).toLocaleDateString()}
                          </div>
                        </td>
                      )}

                      <td style={styles.tableCell}>
                        <button
                          onClick={() => handleViewDetails(escalation)}
                          style={{
                            ...styles.submitBtn,
                            padding: "0.25rem 0.5rem",
                            fontSize: fonts.small,
                            cursor: "pointer",
                          }}
                        >
                          {text[language].viewDetails}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <DetailModal />
      </>
    );
  };

  // ===== ALERT COMPONENT =====
  const Alert = ({ type, message, onClose }) => {
    useEffect(() => {
      const timer = setTimeout(() => onClose(), 5000);
      return () => clearTimeout(timer);
    }, [onClose]);

    return (
      <div
        style={{
          position: "fixed",
          top: screenSize === "xs" ? "0.5rem" : "1.25rem",
          right: screenSize === "xs" ? "0.5rem" : "1.25rem",
          zIndex: 3000,
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.75rem 1rem",
          borderRadius: "0.5rem",
          border: "1px solid",
          maxWidth: screenSize === "xs" ? "calc(100vw - 1rem)" : "25rem",
          backdropFilter: "blur(10px)",
          backgroundColor:
            type === "error"
              ? "rgba(239, 68, 68, 0.1)"
              : "rgba(34, 197, 94, 0.1)",
          borderColor: type === "error" ? "#ef4444" : "#22c55e",
          color: type === "error" ? "#ef4444" : "#22c55e",
        }}
      >
        {type === "error" ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
        <span style={{ fontSize: fonts.body, flex: 1 }}>{message}</span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            padding: "0.25rem",
          }}
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div style={styles.container}>
      {/* Alerts */}
      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      {success && (
        <Alert type="success" message={success} onClose={() => setSuccess("")} />
      )}

      {/* Header with Create Button */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>{text[language].title}</h1>
            {/* <p style={styles.subtitle}>{text[language].subtitle}</p> */}
          </div>

          {/* Create Escalation Button */}
          <button
            style={styles.createBtn}
            onClick={handleCreateEscalation}
            onMouseEnter={(e) => {
              // e.currentTarget.style.transform = "translateY(-2px)";
              // e.currentTarget.style.boxShadow =
              //   "0 0.5rem 1.5rem rgba(46, 124, 186, 0.4)";
            }}
            onMouseLeave={(e) => {
              // e.currentTarget.style.transform = "translateY(0)";
              // e.currentTarget.style.boxShadow =
              //   "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)";
            }}
          >
            <Plus size={screenSize === "xs" ? 18 : 20} />
            {text[language].createEscalation}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "my" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("my")}
        >
          {text[language].myEscalations}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === "assigned" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("assigned")}
        >
          {text[language].assignedEscalations}
        </button>
      </div>

      {activeTab === "my" && (
        <MyEscalationsTable
          loading={loading}
          escalations={escalations}
          screenSize={screenSize}
          language={language}
          text={text}
          theme={theme}
          styles={styles}
          fonts={fonts}
          getStatusBadge={getStatusBadge}
        />
      )}

      {activeTab === "assigned" && <GroupAssignedEscalation
        activeTab={activeTab}
        screenSize={screenSize}
        language={language}
        text={text}
        loading={loading}
        theme={theme}
        styles={styles}
        fonts={fonts}
        getStatusBadge={getStatusBadge}
        apiBaseUrl={apiBaseUrl}
        fetchEscalations={fetchEscalations}
      />}

      {/* Modals */}
      {showCreateModal && (
        <CreateEscalationModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => {
            handleSubmitEscalation(data);
            setShowCreateModal(false);
          }}
          loading={loading}
          theme={theme}
          screenSize={screenSize}
          styles={styles}
          spacing={spacing}
        />
      )}
    </div>
  );
};

export default EscalationComplaints;
